import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {Layout, ResponsiveGridLayout, useContainerWidth, verticalCompactor} from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import {IconChartBar, IconGripVertical, IconInfoCircle, IconLayoutList, IconPencil, IconPlus, IconRefresh, IconTable, IconX} from '@tabler/icons-react';
import {Application, HomeWidget, PinnableScreen, Screen, StatisticWidgetDef} from '../types';
import {apiService} from '../services/api';
import {useHomeWidgets} from '../hooks/useHomeWidgets';
import {appScreenPath} from '../utils/screenPath';
import {initialStatisticFilterValues} from '../utils/statisticValues';
import {StatisticWidgetView, widgetTitle} from './StatisticWidgetView';
import {WidgetSkeleton} from './WidgetSkeleton';
import {isClientAdmin} from '../utils/roles';
import {AppContext} from '../contexts/AppContext';
import {StatisticWidgetEditorModal} from './StatisticWidgetEditorModal';
import {ScreenDataTable} from './ScreenDataTable';
import {useScreenNavigation} from '../hooks/useScreenNavigation';
import './HomeWidgets.css';

const MAX_SPAN = 4;
const ROW_HEIGHT = 132;
type GridBreakpoint = 'lg' | 'md' | 'sm';
// Container widths (px): 4 columns on a desktop, 2 on a tablet, 1 on a phone
const GRID_BREAKPOINTS: Record<GridBreakpoint, number> = {lg: 900, md: 560, sm: 0};
const GRID_COLS: Record<GridBreakpoint, number> = {lg: MAX_SPAN, md: 2, sm: 1};

interface PinnedWidget {
    app: Application;
    screen: Screen;
    definition: StatisticWidgetDef;
    widget: HomeWidget;
}

/** A pinned screen: its table only, with the screen's own filters and default page size. */
interface PinnedScreen {
    app: Application;
    screen: Screen;
    widget: HomeWidget;
}

/** The screen as a table to look at: a row click opens what it opens on the screen, nothing else is offered. */
const displayOnlyScreen = (screen: Screen): Screen => ({
    ...screen,
    createForms: [],
    deleteEnabled: false,
    buttons: [],
    columns: screen.columns.map((column) => ({...column, editable: false, filterable: false})),
} as Screen);

const ScreenWidgetCard: React.FC<PinnedScreen & {
    liveSize: {width: number; height: number} | null;
    onUnpin: () => void;
}> = ({app, screen, liveSize, onUnpin}) => {
    const navigate = useNavigate();
    const tableScreen = useMemo(() => displayOnlyScreen(screen), [screen]);
    // Part of the table's query key: bumping it loads the rows again
    const [refresh, setRefresh] = useState(0);
    // The same row click as on the screen page, with the home page as the breadcrumb origin
    const {handleOpenUpdateForm, handleNavigateToScreen} = useScreenNavigation(String(app.id), screen, {label: 'Prehľad', path: () => '/'});

    return (
        <article className={`home-widget${liveSize ? ' is-resizing' : ''}`}>
            <header className="home-widget-head has-refresh">
                <button type="button" className="home-widget-grip" title="Presunúť widget — potiahnite" aria-label="Presunúť widget">
                    <IconGripVertical size={15}/>
                </button>
                <button type="button" className="home-widget-title" onClick={() => navigate(appScreenPath(app, screen.id))} title="Otvoriť screen">
                    <IconLayoutList size={15}/>
                    <span>{screen.displayName}</span>
                </button>
                <div className="home-widget-actions">
                    <button type="button" className="home-widget-icon" onClick={onUnpin}
                            title="Odobrať z domovskej stránky" aria-label="Odobrať z domovskej stránky">
                        <IconX size={15}/>
                    </button>
                </div>
                <button type="button" className="home-widget-icon home-widget-refresh" onClick={() => setRefresh((value) => value + 1)}
                        title="Obnoviť" aria-label="Obnoviť">
                    <IconRefresh size={15}/>
                </button>
            </header>
            <div className="home-widget-body home-widget-screen" data-e2e={`home-widget-screen:${screen.id}`}>
                <ScreenDataTable screen={tableScreen} embedded displayOnly editable={false} refreshTrigger={refresh}
                                 onOpenUpdateForm={handleOpenUpdateForm} onNavigateToScreen={handleNavigateToScreen}/>
            </div>
            {liveSize && <span className="home-widget-size-badge">{liveSize.width} × {liveSize.height}</span>}
        </article>
    );
};

const WidgetCard: React.FC<PinnedWidget & {
    /** Live spans while the corner handle is dragged, null otherwise. */
    liveSize: {width: number; height: number} | null;
    onUnpin: () => void;
    onEdit: () => void;
}> = ({app, screen, definition, liveSize, onUnpin, onEdit}) => {
    const navigate = useNavigate();
    const filters = useMemo(() => screen.statisticFilters ?? [], [screen]);
    const values = useMemo(() => initialStatisticFilterValues(filters), [filters]);
    const missing = filters.filter((filter) => filter.required && !values[filter.code]);

    // Same key as StatisticView, so opening the statistic reuses the widget's result
    const {data, isLoading, error} = useQuery({
        queryKey: ['statisticData', screen.id, values],
        queryFn: ({signal}) => apiService.getStatisticData(screen.id, values, signal),
        enabled: missing.length === 0,
    });

    const failed = error || (data && data.success === false);
    const columns = data?.columns ?? [];
    const rows = data?.rows ?? [];
    let body: React.ReactNode;
    if (missing.length > 0) {
        body = <p className="home-widget-note">Widget potrebuje hodnotu filtra {missing.map((filter) => `„${filter.displayName}“`).join(', ')}. Nastavte predvolenú hodnotu v štatistike.</p>;
    } else if (isLoading) {
        body = <WidgetSkeleton type={definition.type}/>;
    } else if (failed) {
        body = <p className="home-widget-note home-widget-error">{data?.error || 'Štatistiku sa nepodarilo načítať.'}</p>;
    } else {
        body = <StatisticWidgetView widget={definition} columns={columns} rows={rows} height="fill"/>;
    }

    return (
        <article className={`home-widget${liveSize ? ' is-resizing' : ''}`}>
            <header className="home-widget-head">
                <button type="button" className="home-widget-grip" title="Presunúť widget — potiahnite" aria-label="Presunúť widget">
                    <IconGripVertical size={15}/>
                </button>
                <button type="button" className="home-widget-title" onClick={() => navigate(appScreenPath(app, screen.id))} title="Otvoriť štatistiku">
                    {definition.type === 'table' ? <IconTable size={15}/> : <IconChartBar size={15}/>}
                    <span>{widgetTitle(definition, screen)}</span>
                </button>
                <div className="home-widget-actions">
                    {!definition.displayName && screen.description && (
                        <OverlayTrigger placement="bottom" trigger={['hover', 'focus']}
                                        overlay={<Tooltip id={`home-widget-info-${definition.id}`}>{screen.description}</Tooltip>}>
                            <button type="button" className="home-widget-icon" aria-label="Popis štatistiky">
                                <IconInfoCircle size={15}/>
                            </button>
                        </OverlayTrigger>
                    )}
                    {isClientAdmin() && (
                        <button type="button" className="home-widget-icon" title="Upraviť widget" aria-label="Upraviť widget" onClick={onEdit}>
                            <IconPencil size={15}/>
                        </button>
                    )}
                    <button type="button" className="home-widget-icon" onClick={onUnpin}
                            title="Odobrať z domovskej stránky" aria-label="Odobrať z domovskej stránky">
                        <IconX size={15}/>
                    </button>
                </div>
            </header>
            <div className="home-widget-body">{body}</div>
            {liveSize && <span className="home-widget-size-badge">{liveSize.width} × {liveSize.height}</span>}
        </article>
    );
};

/**
 * „Pridať widget“: the widgets of all visible statistics, grouped by statistic, and
 * the screens of the user's applications, grouped by application - both without
 * what is already pinned. Screens are loaded when the menu opens.
 */
const AddWidgetMenu: React.FC<{
    available: Omit<PinnedWidget, 'widget'>[];
    pinnedScreenIds: Set<string>;
    onPick: (source: {widgetId: string} | {screenId: string}) => void;
}> = ({available, pinnedScreenIds, onPick}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const boxRef = useRef<HTMLDivElement | null>(null);

    const {data: screens, isLoading: screensLoading} = useQuery({
        queryKey: ['homeWidgetScreens'],
        queryFn: async (): Promise<PinnableScreen[]> => {
            const response = await apiService.getPinnableScreens();
            return response.success && Array.isArray(response.data) ? response.data : [];
        },
        enabled: open,
    });

    useEffect(() => {
        if (!open) return;
        const close = (event: MouseEvent) => {
            if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    const needle = search.trim().toLocaleLowerCase('sk');
    const matches = (...texts: string[]) => needle === '' || texts.some((text) => text.toLocaleLowerCase('sk').includes(needle));

    const groups = new Map<string, Omit<PinnedWidget, 'widget'>[]>();
    available.filter((item) => matches(widgetTitle(item.definition, item.screen), item.screen.displayName)).forEach((item) => {
        const key = String(item.screen.id);
        groups.set(key, [...(groups.get(key) ?? []), item]);
    });

    const screenGroups = new Map<string, PinnableScreen[]>();
    (screens ?? []).filter((item) => !pinnedScreenIds.has(item.screenId) && matches(item.displayName, item.applicationName)).forEach((item) => {
        screenGroups.set(item.applicationId, [...(screenGroups.get(item.applicationId) ?? []), item]);
    });

    const close = () => { setOpen(false); setSearch(''); };

    return (
        <div className="home-widgets-add" ref={boxRef}>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => (open ? close() : setOpen(true))} aria-expanded={open}>
                <IconPlus size={14} className="me-1"/>Pridať widget
            </button>
            {open && (
                <div className="home-widgets-add-menu" role="menu">
                    <input type="search" className="form-control form-control-sm home-widgets-add-search" placeholder="Hľadať…"
                           aria-label="Hľadať widget alebo screen" autoFocus value={search} onChange={(e) => setSearch(e.target.value)}/>
                    {groups.size > 0 && <div className="home-widgets-add-section">Štatistiky</div>}
                    {Array.from(groups.values()).map((items) => (
                        <div key={String(items[0].screen.id)} className="home-widgets-add-group">
                            <div className="home-widgets-add-group-name">{items[0].screen.displayName}</div>
                            {items.map((item) => (
                                <button key={item.definition.id} type="button" role="menuitem" className="home-widgets-add-item"
                                        onClick={() => { onPick({widgetId: String(item.definition.id)}); close(); }}>
                                    {item.definition.type === 'table' ? <IconTable size={15}/> : <IconChartBar size={15}/>}
                                    <span>{widgetTitle(item.definition, item.screen)}</span>
                                </button>
                            ))}
                        </div>
                    ))}
                    {screenGroups.size > 0 && <div className="home-widgets-add-section">Screeny</div>}
                    {Array.from(screenGroups.values()).map((items) => (
                        <div key={items[0].applicationId} className="home-widgets-add-group">
                            <div className="home-widgets-add-group-name">{items[0].applicationName}</div>
                            {items.map((item) => (
                                <button key={item.screenId} type="button" role="menuitem" className="home-widgets-add-item"
                                        data-e2e={`home-widgets-add-screen:${item.screenId}`}
                                        onClick={() => { onPick({screenId: item.screenId}); close(); }}>
                                    <IconLayoutList size={15}/>
                                    <span>{item.displayName}</span>
                                </button>
                            ))}
                        </div>
                    ))}
                    {screensLoading && <p className="home-widgets-add-empty">Načítavam screeny…</p>}
                    {!screensLoading && groups.size === 0 && screenGroups.size === 0 && (
                        <p className="home-widgets-add-empty">{needle ? 'Nič sa nenašlo.' : 'Všetko je už na domovskej stránke.'}</p>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * The user's pinned widgets at the top of the home page: statistic widgets (table
 * or chart, as defined on the statistic) and screens (the screen's table).
 * Width/height are grid spans the user sets per pin (stored server-side).
 */
export const HomeWidgets: React.FC<{applications: Application[]}> = ({applications}) => {
    const {widgets, pin, unpin, saveLayout, isLoading} = useHomeWidgets();
    const {reloadApplications} = useContext(AppContext) ?? {};
    const queryClient = useQueryClient();
    const [editing, setEditing] = useState<{statisticId: string; widgetId: string} | null>(null);
    const {width, containerRef, mounted} = useContainerWidth();
    const [breakpoint, setBreakpoint] = useState<GridBreakpoint>('lg');
    const [liveSize, setLiveSize] = useState<{id: string; width: number; height: number} | null>(null);

    const definitions = useMemo(() => {
        const byId = new Map<string, Omit<PinnedWidget, 'widget'>>();
        applications.forEach((app) => {
            const screen = app.screens?.[0];
            if (app.project?.code === 'statistics' && screen?.style === 'statistic') {
                (screen.widgets ?? []).forEach((definition) => byId.set(String(definition.id), {app, screen, definition}));
            }
        });
        return byId;
    }, [applications]);

    const screensById = useMemo(() => {
        const byId = new Map<string, {app: Application; screen: Screen}>();
        applications.forEach((app) => (app.screens ?? []).forEach((screen) => {
            if (!byId.has(String(screen.id))) byId.set(String(screen.id), {app, screen});
        }));
        return byId;
    }, [applications]);

    // A pin whose statistic or screen is not in /applications (yet) is left out
    type Pinned = ({kind: 'statistic'} & PinnedWidget) | ({kind: 'screen'} & PinnedScreen);
    const pinned: Pinned[] = widgets.flatMap<Pinned>((widget) => {
        if (widget.kind === 'screen') {
            const found = screensById.get(String(widget.screenId));
            return found ? [{kind: 'screen' as const, ...found, widget}] : [];
        }
        const found = definitions.get(String(widget.widgetId));
        return found ? [{kind: 'statistic' as const, ...found, widget}] : [];
    });

    if (isLoading) {
        return (
            <section className="home-widgets-skeleton" aria-label="Moje widgety" aria-busy="true">
                {[0, 1].map((index) => (
                    <article key={index} className="home-widget">
                        <header className="home-widget-head"><span className="widget-skeleton-block" style={{width: '40%', height: 14}}/></header>
                        <div className="home-widget-body"><WidgetSkeleton type={index === 0 ? 'bar' : 'table'}/></div>
                    </article>
                ))}
            </section>
        );
    }

    const pinnedWidgetIds = new Set(widgets.map((widget) => String(widget.widgetId ?? '')));
    const pinnedScreenIds = new Set(widgets.map((widget) => String(widget.screenId ?? '')));
    const available = Array.from(definitions.values()).filter((item) => !pinnedWidgetIds.has(String(item.definition.id)));
    const toolbar = (
        <div className="home-widgets-toolbar">
            <span className="home-widgets-toolbar-title">Moje widgety</span>
            <AddWidgetMenu available={available} pinnedScreenIds={pinnedScreenIds} onPick={(source) => pin(source).catch(() => undefined)}/>
        </div>
    );

    if (pinned.length === 0) {
        return (
            <>
                {toolbar}
                <p className="home-widgets-hint">Grafy a tabuľky zo štatistík alebo tabuľky screenov si sem pridáte tlačidlom „Pridať widget“.</p>
            </>
        );
    }

    // Positions are edited on the 4-column grid only; narrower screens derive their stacking from it
    const editable = breakpoint === 'lg';
    const layout: Layout = pinned.map(({widget}) => ({
        i: widget.id, x: widget.x, y: widget.y, w: widget.width, h: widget.height,
        minW: 1, maxW: MAX_SPAN, minH: 1, maxH: MAX_SPAN,
    }));
    const commit = (next: Layout) => {
        setLiveSize(null);
        const changed = next.some((item) => {
            const current = layout.find((existing) => existing.i === item.i);
            return !current || current.x !== item.x || current.y !== item.y || current.w !== item.w || current.h !== item.h;
        });
        if (changed) {
            saveLayout(next.map((item) => ({id: item.i, x: item.x, y: item.y, width: item.w, height: item.h})));
        }
    };

    return (
        <>
        {toolbar}
        <div ref={containerRef}>
            {mounted && (
                <ResponsiveGridLayout<GridBreakpoint>
                    className="home-widgets"
                    width={width}
                    breakpoints={GRID_BREAKPOINTS}
                    cols={GRID_COLS}
                    layouts={{lg: layout}}
                    rowHeight={ROW_HEIGHT}
                    margin={[12, 12]}
                    containerPadding={[0, 0]}
                    compactor={verticalCompactor}
                    dragConfig={{enabled: editable, handle: '.home-widget-grip'}}
                    resizeConfig={{enabled: editable, handles: ['se']}}
                    onBreakpointChange={(next) => setBreakpoint(next)}
                    onResize={(_layout, _old, item) => item && setLiveSize({id: item.i, width: item.w, height: item.h})}
                    onResizeStop={(next) => commit(next)}
                    onDragStop={(next) => commit(next)}
                >
                    {pinned.map((item) => (
                        <div key={item.widget.id} className="home-widget-cell">
                            {item.kind === 'screen' ? (
                                <ScreenWidgetCard {...item}
                                                  liveSize={liveSize?.id === item.widget.id ? liveSize : null}
                                                  onUnpin={() => unpin(item.widget.id).catch(() => undefined)}/>
                            ) : (
                                <WidgetCard {...item}
                                            liveSize={liveSize?.id === item.widget.id ? liveSize : null}
                                            onUnpin={() => unpin(item.widget.id).catch(() => undefined)}
                                            onEdit={() => setEditing({statisticId: String(item.screen.id), widgetId: String(item.definition.id)})}/>
                            )}
                        </div>
                    ))}
                </ResponsiveGridLayout>
            )}
        </div>
        {editing && (
            <StatisticWidgetEditorModal show statisticId={editing.statisticId} widgetId={editing.widgetId}
                                        onHide={() => setEditing(null)}
                                        onSaved={() => {
                                            setEditing(null);
                                            // widget definitions travel in /applications; a deleted widget also drops its pins
                                            reloadApplications?.();
                                            queryClient.invalidateQueries({queryKey: ['homeWidgets']});
                                        }}/>
        )}
        </>
    );
};
